// ── Forward Curves ─────────────────────────────────────────────────────────
//
// What we thought a rate would do, as of several different upload dates, on one
// pair of axes.
//
// A curve is one (index, curvedate) pair: curvedate is the day it was uploaded,
// ratedate is the future month a rate applies to. Comparing two curvedates for
// one index asks "what did we expect then, versus what do we expect now" — the
// question behind every projected interest figure on the performance page, and
// behind the divergence between a loan's original and current IRR.
//
// ONE INDEX AT A TIME, DELIBERATELY.
//
//   SOFR and Prime differ by hundreds of basis points, so drawing them together
//   compresses both into flat lines at opposite ends of the plot and the shape
//   — which is the whole point of looking — disappears. Two measures of
//   different scale get two charts, not two axes. The index picker changes
//   which chart you are looking at.
//
// TWO MODES
//
//   Rates    the curves as uploaded, in percent.
//   Spread   every curve minus a baseline curve, in basis points.
//
//   The second exists because the first hides the answer. Two SOFR curves a
//   month apart sit within a few basis points of each other on a plot scaled to
//   4%; the lines overlap and a 15bp shift is invisible. Subtracting the
//   baseline rescales the y-axis to the difference itself, which is what a
//   reader comparing upload dates actually wants to see.
//
// COLOUR, AND WHY IT IS NOT THE ONLY ENCODING
//
//   The brand palette is two hues. It carries five or six lines apart under
//   colour-vision deficiency (validated: worst adjacent pair ΔE 17.9), but
//   several of its members are pale, and a 2px line at 1.7:1 against white is
//   not something to rely on. So every series carries a DASH PATTERN as well as
//   a colour, the last point of each line is labelled directly, and a table
//   view shows the numbers outright. Any one of those three is enough to tell
//   the curves apart; colour alone never has to be.
//
// THE ACTUAL RATE IS NOT A SEVENTH FORECAST.
//
//   dbo.Rates says what the index really did. Where an old curve's rate dates
//   have since happened, that is the only line on the plot that is not an
//   opinion — so it is drawn as a reference, not as another member of the
//   categorical ramp: near-black, solid, heavier, and always first in the
//   legend. Giving it a brand colour would put it in the same visual class as
//   the guesses it exists to judge.
//
//   In spread mode it is the forecast ERROR line: actual minus the baseline
//   curve is how wrong that curve turned out to be, in basis points.

import { useState, useEffect, useMemo, useCallback, useRef, Fragment } from "react";
import { B, INK_MUTED, INK_PRIMARY, SURFACE, SURFACE_MUTED,
         HAIRLINE, HAIRLINE_FIRM } from "../../../shared/theme";
import { Card, Spinner, ErrorMsg, FilterSelect, Btn } from "../../../shared/ui";
import { fmtDate, exportCSV } from "../../../shared/format";
import { useApp } from "../../../shared/appContext";
import { useChartTheme, ChartThemeToggle, chartCardStyle, useWidth } from "../../../shared/chartTheme";

const num = v => { const n = parseFloat(v); return isFinite(n) ? n : 0; };
const pct = r => (r === null || r === undefined || !isFinite(r))
  ? "—" : (r * 100).toFixed(3) + "%";
const bps = d => (d === null || d === undefined || !isFinite(d))
  ? "—" : (d >= 0 ? "+" : "−") + Math.abs(d * 10000).toFixed(1);

// ── SERIES STYLING ─────────────────────────────────────────────────────────
//
// Order and colours come from the brand tokens, re-sequenced for LINES rather
// than fills: the darkest, most saturated members first, because a 2px stroke
// has a fraction of the area a bar does and the pale tints that read fine as a
// filled shape vanish as a line.
//
// The dash pattern is not decoration. It is the secondary encoding that keeps
// the chart readable in greyscale, in forced-colours mode, and for a reader who
// cannot separate the two brand hues — the case the palette validator flags as
// needing relief. Solid is first because a single-curve chart should not look
// dashed for no reason.
//
// B.* tokens carry literal fallbacks, which matters here: inside an <svg> a
// var() that does not resolve makes stroke `none` and fill BLACK.
const SERIES = [
  { color: B.cyanDark,  dash: null },
  { color: B.amberDark, dash: "7 3" },
  { color: B.cyan,      dash: "2 3" },
  { color: B.amber,     dash: "9 3 2 3" },
  { color: B.sidebar,   dash: "1 3" },
  { color: B.black40,   dash: "11 4" },
];
const styleAt = i => SERIES[((i % SERIES.length) + SERIES.length) % SERIES.length];

// Deliberately NOT from the categorical ramp. Ink, solid, and heavier than the
// forecasts — the one line on the plot that is a fact rather than a forecast
// should not look like one of them.
const ACTUAL_STYLE = { color: INK_PRIMARY, dash: null, width: 2.75 };

// Past this, colour and dash together stop carrying the distinction. The picker
// refuses rather than drawing a seventh line nobody can follow.
const MAX_SERIES = SERIES.length;

// Dark theme: the same series, swapped for the bright step of each hue — the
// deep brand tones vanish on the navy card. Dashes are unchanged, so the
// secondary encoding still holds.
const DARK_SERIES = {
  [B.cyanDark]: "#38d6f5", [B.amberDark]: "#ffc073", [B.cyan]: "#9be9fa",
  [B.amber]: "#ffd479", [B.sidebar]: "#a5b4fc", [B.black40]: "#cbd5e1",
  [INK_PRIMARY]: "#ffffff",
};

// ── plot geometry ──────────────────────────────────────────────────────────
const PAD = { t: 26, r: 132, b: 34, l: 58 };   // r holds end labels, t the axis caption
// Height is fixed; width is the card's real pixel width, measured in the
// component (shared/chartTheme useWidth). A fixed-width viewBox stretched to
// fit scaled the axis text with the window.
const VB_H = 340;

const ymOf = d => String(d || "").slice(0, 7);
const MONTHS = ["Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"];
const monthLabel = ym => {
  const [y, m] = String(ym).split("-");
  return MONTHS[parseInt(m, 10) - 1] + " " + String(y).slice(2);
};

// Dates are ISO strings throughout; comparing and differencing them as numbers
// needs one conversion, done here so nothing downstream parses a date twice.
const dnum = s => Date.parse(s + "T00:00:00Z");

// "Nice" axis ticks — a round step near the requested count, so the gridlines
// land on values a reader recognises rather than on 3.7142%.
function ticks(lo, hi, count) {
  if (!isFinite(lo) || !isFinite(hi)) return [];
  if (lo === hi) return [lo];
  const raw = (hi - lo) / Math.max(1, count);
  const mag = Math.pow(10, Math.floor(Math.log10(raw)));
  const norm = raw / mag;
  const step = (norm >= 5 ? 10 : norm >= 2 ? 5 : norm >= 1 ? 2 : 1) * mag;
  const out = [];
  for (let v = Math.ceil(lo / step) * step; v <= hi + step * 1e-9; v += step) out.push(v);
  return out;
}

export const ForwardCurvesPage = () => {
  const { api } = useApp();

  const [cat, setCat]         = useState(null);   // catalogue + published run
  const [err, setErr]         = useState("");
  const [busy, setBusy]       = useState(true);
  const [indexname, setIndexname] = useState("");
  const [picked, setPicked]   = useState([]);     // curvedates, in pick order
  const [series, setSeries]   = useState(null);
  const [seriesBusy, setSeriesBusy] = useState(false);
  const [mode, setMode]       = useState("rates"); // "rates" | "spread"
  const [baseline, setBaseline] = useState("");
  const [showTable, setShowTable] = useState(false);
  const [hover, setHover]     = useState(null);   // { x, ratedate }
  const T = useChartTheme();
  const sc = c => (T.dark ? (DARK_SERIES[c] || c) : c);
  const [plotBox, vbw] = useWidth(900);
  const VB = { w: vbw, h: VB_H };
  const [showActual, setShowActual] = useState(true);

  // ── the catalogue ────────────────────────────────────────────────────────
  const load = useCallback(() => {
    setBusy(true); setErr("");
    return api.get("getForwardCurves")
      .then(d => { setCat(d); setBusy(false); return d; })
      .catch(e => { setErr(e.message); setBusy(false); return null; });
  }, [api]);

  useEffect(() => { load(); }, [load]);

  // Open on the index the published run was built from when there is one — that
  // is the curve the numbers everyone quotes were projected off.
  useEffect(() => {
    if (!cat || indexname || !cat.indexes || !cat.indexes.length) return;
    setIndexname(cat.indexes.includes("SOFR") ? "SOFR" : cat.indexes[0]);
  }, [cat, indexname]);

  const curvesFor = useMemo(
    () => (cat && cat.catalog ? cat.catalog.filter(c => c.indexname === indexname) : []),
    [cat, indexname]);

  // Changing index invalidates the selection: a curvedate is only meaningful
  // paired with its index, and carrying dates across would silently plot
  // whichever of them happened to exist on both.
  useEffect(() => {
    if (!curvesFor.length) { setPicked([]); return; }
    const runCurve = cat && cat.publishedRun && cat.publishedRun.curvedate;
    const have = curvesFor.map(c => c.curvedate);
    // The newest, plus the run's curve when it is a different one — the two a
    // reader almost always wants side by side on arrival.
    const seed = [have[0]];
    if (runCurve && have.includes(runCurve) && runCurve !== have[0]) seed.push(runCurve);
    setPicked(seed);
  }, [curvesFor, cat]);

  // Baseline defaults to the OLDEST selected curve, so spreads read as "how far
  // it has moved since then" rather than "how far it had to go".
  useEffect(() => {
    if (!picked.length) { setBaseline(""); return; }
    if (picked.includes(baseline)) return;
    setBaseline([...picked].sort()[0]);
  }, [picked, baseline]);

  // ── the points ───────────────────────────────────────────────────────────
  const seq = useRef(0);
  const pickKey = picked.join(",");
  useEffect(() => {
    if (!indexname || !pickKey) { setSeries(null); return; }
    const mine = ++seq.current;
    setSeriesBusy(true);
    api.get("getForwardCurves", { indexname, curvedates: pickKey })
      .then(d => { if (mine === seq.current) { setSeries(d); setSeriesBusy(false); } })
      .catch(e => { if (mine === seq.current) { setErr(e.message); setSeriesBusy(false); } });
  }, [api, indexname, pickKey]);

  // ── the actual rate, by month ────────────────────────────────────────────
  // Its own call: the whole history dbo.Rates holds for the index, not just the
  // plotted window, so it does not wait on (or reload with) the curve picks.
  const [hist, setHist] = useState(null);         // { months, scale } | { error }
  const [histBusy, setHistBusy] = useState(false);
  useEffect(() => {
    if (!indexname) { setHist(null); return; }
    let live = true;
    setHistBusy(true);
    api.get("getForwardCurves", { indexname, history: "monthly" })
      .then(d => { if (live) { setHist(d.actualMonthly
                                 || { error: d.actualMonthlyError || "No data returned." });
                               setHistBusy(false); } })
      .catch(e => { if (live) { setHist({ error: e.message }); setHistBusy(false); } });
    return () => { live = false; };
  }, [api, indexname]);

  const toggleCurve = cd => {
    setPicked(p => {
      if (p.includes(cd)) return p.filter(x => x !== cd);
      if (p.length >= MAX_SERIES) return p;      // the cap is silent-refuse, and
      return [...p, cd];                         // the notice below says why
    });
  };

  // ── what gets drawn ──────────────────────────────────────────────────────
  //
  // Baseline lookup is by ratedate, so a curve with a different point spacing
  // than the baseline still differences correctly — and a ratedate the baseline
  // does not cover yields no point rather than a spread against zero.
  const plot = useMemo(() => {
    const raw = (series && series.series) || [];
    if (!raw.length) return null;

    const baseMap = new Map();
    if (mode === "spread") {
      const b = raw.find(s => s.curvedate === baseline);
      if (!b) return null;
      b.points.forEach(p => baseMap.set(p.ratedate, num(p.rate)));
    }

    // The reference series is built first so it can be prepended: it leads the
    // legend, and drawing it FIRST would put it under the forecasts — it is the
    // line you check the others against, so it goes on top.
    const actualPts = (showActual && series && series.actual
                       && series.actual.points) || [];

    const lines = raw.map((s, i) => {
      const pts = [];
      for (const p of s.points) {
        const v = mode === "spread"
          ? (baseMap.has(p.ratedate) ? num(p.rate) - baseMap.get(p.ratedate) : null)
          : num(p.rate);
        if (v === null) continue;
        pts.push({ x: dnum(p.ratedate), y: v, ratedate: p.ratedate });
      }
      return { curvedate: s.curvedate, style: styleAt(i), pts,
               isBaseline: mode === "spread" && s.curvedate === baseline };
    }).filter(l => l.pts.length);

    if (actualPts.length) {
      const pts = [];
      for (const p of actualPts) {
        const v = mode === "spread"
          ? (baseMap.has(p.ratedate) ? num(p.rate) - baseMap.get(p.ratedate) : null)
          : num(p.rate);
        if (v === null) continue;
        pts.push({ x: dnum(p.ratedate), y: v, ratedate: p.ratedate });
      }
      // Appended so it draws last (on top); the legend renders it first
      // separately, because reading order and paint order are different needs.
      if (pts.length) lines.push({ curvedate: "actual", label: "Actual",
                                   isActual: true, style: ACTUAL_STYLE, pts });
    }

    if (!lines.length) return null;

    let xLo = Infinity, xHi = -Infinity, yLo = Infinity, yHi = -Infinity;
    lines.forEach(l => l.pts.forEach(p => {
      if (p.x < xLo) xLo = p.x; if (p.x > xHi) xHi = p.x;
      if (p.y < yLo) yLo = p.y; if (p.y > yHi) yHi = p.y;
    }));

    // A flat series would otherwise divide by zero; give it a band to sit in.
    if (yLo === yHi) { yLo -= 0.0005; yHi += 0.0005; }
    // In spread mode zero is a real reference — the baseline itself — so the
    // scale always includes it, otherwise "no change" floats somewhere odd.
    if (mode === "spread") { yLo = Math.min(yLo, 0); yHi = Math.max(yHi, 0); }
    const padY = (yHi - yLo) * 0.08;
    yLo -= padY; yHi += padY;

    const W = vbw - PAD.l - PAD.r, H = VB_H - PAD.t - PAD.b;
    const sx = x => PAD.l + (xHi === xLo ? 0 : ((x - xLo) / (xHi - xLo)) * W);
    const sy = y => PAD.t + H - ((y - yLo) / (yHi - yLo)) * H;

    lines.forEach(l => {
      l.d = l.pts.map((p, i) => (i ? "L" : "M") + sx(p.x).toFixed(2)
                                   + " " + sy(p.y).toFixed(2)).join(" ");
      l.end = l.pts[l.pts.length - 1];
    });

    // ── DECLUTTER THE END LABELS ────────────────────────────────────────
    //
    // Curves that converge — which is most of them, since a forward curve
    // flattens out — end within a few basis points of each other, and their
    // labels land on the same pixel row and overprint. Rendered, three curves
    // produced "08/01/26" and "06/01/26" stacked illegibly on one line.
    //
    // So the labels are pushed apart to a minimum spacing: sort by where they
    // want to be, walk down enforcing the gap, then if the stack has run past
    // the bottom of the plot, walk back up. Each label keeps its ORDER — the
    // topmost line still gets the topmost label — so the displacement never
    // makes a label point at the wrong curve, and a leader line is drawn
    // whenever one has moved far enough to be ambiguous.
    const GAP = 13;
    const placed = lines
      .map((l, i) => ({ i, want: sy(l.end.y) }))
      .sort((a, b) => a.want - b.want);
    let prev = -Infinity;
    placed.forEach(p => { p.y = Math.max(p.want, prev + GAP); prev = p.y; });
    const overflow = prev - (PAD.t + H);
    if (overflow > 0) {
      // Shift the whole stack up, then re-enforce upward so it cannot pile up
      // against the top edge either.
      let next = Infinity;
      for (let k = placed.length - 1; k >= 0; k--) {
        placed[k].y = Math.min(placed[k].y - overflow, next - GAP);
        next = placed[k].y;
      }
    }
    placed.forEach(p => {
      lines[p.i].labelY = p.y;
      lines[p.i].labelMoved = Math.abs(p.y - p.want) > 3;
    });

    // Every distinct ratedate across all series, for the crosshair to snap to.
    const xs = [...new Set(lines.flatMap(l => l.pts.map(p => p.x)))].sort((a, b) => a - b);

    return { lines, sx, sy, xLo, xHi, yLo, yHi, W, H, xs,
             yTicks: ticks(yLo, yHi, 5) };
  }, [series, mode, baseline, showActual, vbw]);

  // ── the crosshair ────────────────────────────────────────────────────────
  // Snaps to the nearest real ratedate rather than following the pointer
  // freely: the values in the tooltip are then the values on the curves, not an
  // interpolation the data never contained.
  const svgRef = useRef(null);
  const onMove = e => {
    if (!plot || !svgRef.current) return;
    const r = svgRef.current.getBoundingClientRect();
    const vx = ((e.clientX - r.left) / r.width) * VB.w;
    let best = null, bestD = Infinity;
    for (const x of plot.xs) {
      const d = Math.abs(plot.sx(x) - vx);
      if (d < bestD) { bestD = d; best = x; }
    }
    if (best !== null) setHover({ x: best });
  };

  const at = (line, x) => {
    const p = line.pts.find(q => q.x === x);
    return p ? p.y : null;
  };

  const csv = () => {
    if (!plot) return;
    const rows = [];
    plot.xs.forEach(x => {
      const row = { ratedate: new Date(x).toISOString().slice(0, 10) };
      plot.lines.forEach(l => {
        const v = at(l, x);
        row[l.curvedate] = v === null ? "" : (mode === "spread" ? v * 10000 : v);
      });
      rows.push(row);
    });
    exportCSV(rows, ["ratedate", ...plot.lines.map(l => l.curvedate)],
      `forward_curves_${indexname}_${mode}.csv`);
  };

  // ── render ───────────────────────────────────────────────────────────────
  if (busy) return <Spinner />;
  if (err && !cat) return <ErrorMsg msg={err} onRetry={() => load()} />;

  const runCurve = cat && cat.publishedRun && cat.publishedRun.curvedate;
  const newestForIndex = curvesFor.length ? curvesFor[0].curvedate : null;
  const runIsStale = !!(runCurve && newestForIndex && runCurve < newestForIndex);

  return (
    <div style={{ display:"flex", flexDirection:"column", gap:12 }}>
      <SectionTitle>
        Forward Curves
        <span style={{ fontWeight:400, color:INK_MUTED, marginLeft:8,
                       textTransform:"none", letterSpacing:0 }}>
          what each upload expected the index to do — {curvesFor.length} curve
          {curvesFor.length === 1 ? "" : "s"} loaded for {indexname || "—"}
        </span>
      </SectionTitle>

      {err && <ErrorMsg msg={err} onRetry={() => load()} />}

      {(!cat || !cat.indexes || !cat.indexes.length) && (
        <Card style={{ padding:"28px 16px", textAlign:"center",
                       color:INK_MUTED, fontSize:13 }}>
          No forward rates have been loaded yet. Once a curve is uploaded it
          appears here, and every upload after it can be compared against it.
        </Card>
      )}

      {!!(cat && cat.indexes && cat.indexes.length) && (
        <>
          {/* ── controls ──────────────────────────────────────────────── */}
          <Card style={{ padding:"12px 14px" }}>
            <div style={{ display:"flex", flexWrap:"wrap", gap:12, alignItems:"center" }}>
              <FilterSelect label="Index" value={indexname} onChange={setIndexname}
                            allLabel={null}
                            options={cat.indexes.map(i => ({ value:i, label:i }))} />

              <div style={{ display:"flex", alignItems:"center", gap:6, fontSize:13 }}>
                <span style={{ fontSize:11, fontWeight:700, color:INK_MUTED,
                               textTransform:"uppercase", letterSpacing:"0.06em" }}>Show</span>
                <Btn onClick={() => setMode("rates")}
                     variant={mode === "rates" ? "primary" : undefined}
                     title="The curves as uploaded, in percent.">Rates</Btn>
                <Btn onClick={() => setMode("spread")}
                     variant={mode === "spread" ? "primary" : undefined}
                     disabled={picked.length < 2}
                     title={picked.length < 2
                       ? "Pick a second curve to compare against."
                       : "Each curve minus the baseline, in basis points."}>
                  Spread</Btn>
              </div>

              {/* Only offered when there is something to show. An actual series
                  exists only where a selected curve's rate dates have already
                  happened, so on a book of nothing but future-dated curves this
                  control would be a switch that does nothing. */}
              {!!(series && series.actual && series.actual.points
                  && series.actual.points.length) && (
                <label style={{ display:"inline-flex", alignItems:"center", gap:6,
                                fontSize:12, color:INK_MUTED, whiteSpace:"nowrap" }}
                       title={"The rate the index actually set, from dbo.Rates, over the "
                            + "span these curves cover."}>
                  <input type="checkbox" checked={showActual}
                         onChange={e => setShowActual(e.target.checked)} />
                  Actual
                </label>
              )}

              {mode === "spread" && picked.length >= 2 && (
                <FilterSelect label="Baseline" value={baseline} onChange={setBaseline}
                              allLabel={null}
                              options={[...picked].sort().map(c =>
                                ({ value:c, label:fmtDate(c) }))} />
              )}

              <div style={{ display:"flex", gap:8, marginLeft:"auto" }}>
                <Btn onClick={() => setShowTable(t => !t)}>
                  {showTable ? "Hide numbers" : "Show numbers"}
                </Btn>
                <Btn onClick={csv} disabled={!plot}>Export CSV</Btn>
              </div>
            </div>

            {/* ── the curve picker ─────────────────────────────────────── */}
            <div style={{ marginTop:10, paddingTop:10,
                          borderTop:"1px solid " + HAIRLINE,
                          display:"flex", flexWrap:"wrap", gap:6 }}>
              {curvesFor.map(c => {
                const on = picked.includes(c.curvedate);
                const i  = picked.indexOf(c.curvedate);
                const st = on ? styleAt(i) : null;
                const isRun = c.curvedate === runCurve;
                return (
                  <button key={c.curvedate} onClick={() => toggleCurve(c.curvedate)}
                    title={`${c.points} points, ${fmtDate(c.firstdate)} to ${fmtDate(c.lastdate)}`
                         + (isRun ? " · the published performance run was built on this curve" : "")}
                    style={{ display:"inline-flex", alignItems:"center", gap:6,
                             padding:"5px 9px", borderRadius:999, cursor:"pointer",
                             fontSize:12, fontVariantNumeric:"tabular-nums",
                             background: on ? SURFACE_MUTED : SURFACE,
                             color: INK_PRIMARY,
                             border:"1px solid " + (on ? HAIRLINE_FIRM : HAIRLINE) }}>
                    {/* The swatch is a LINE SAMPLE, dash and all, so the legend
                        key matches what is actually drawn rather than implying
                        a solid stroke. */}
                    <svg width="18" height="8" aria-hidden="true">
                      <line x1="0" y1="4" x2="18" y2="4"
                            stroke={st ? sc(st.color) : HAIRLINE_FIRM}
                            strokeWidth="2"
                            strokeDasharray={st && st.dash ? st.dash : undefined} />
                    </svg>
                    {fmtDate(c.curvedate)}
                    {isRun && <span style={{ fontSize:10, color:INK_MUTED }}>· run</span>}
                  </button>
                );
              })}
            </div>

            {picked.length >= MAX_SERIES && (
              <div style={{ marginTop:8, fontSize:12, color:INK_MUTED }}>
                Six curves is the limit. Past that, colour and dash together stop
                telling the lines apart — compare a different pair rather than
                adding a seventh.
              </div>
            )}
          </Card>

          {/* ── the run's curve ───────────────────────────────────────── */}
          {runIsStale && (
            <div style={{ padding:"10px 12px", borderRadius:8, fontSize:12,
                          lineHeight:1.6, background:"#fff8e1",
                          border:"1px solid #f0c000", color:"#6b4e00" }}>
              <strong>The published run is on an older curve.</strong>{" "}
              Run {cat.publishedRun.id} was built on {fmtDate(runCurve)}; the newest{" "}
              {indexname} upload is {fmtDate(newestForIndex)}. Every projected
              interest figure in that run came off the older one — select both
              above to see what moved between them.
            </div>
          )}

          {/* ── the chart ─────────────────────────────────────────────── */}
          <Card style={{ padding:"12px 14px", ...chartCardStyle(T) }}>
            <div style={{ display:"flex", justifyContent:"flex-end", marginBottom:4 }}>
              <ChartThemeToggle />
            </div>
            {seriesBusy && <Spinner />}

            {!seriesBusy && !picked.length && (
              <div style={{ padding:"36px 16px", textAlign:"center",
                            color:T.muted, fontSize:13 }}>
                Pick a curve above to plot it.
              </div>
            )}

            {!seriesBusy && !!picked.length && !plot && (
              <div style={{ padding:"36px 16px", textAlign:"center",
                            color:T.muted, fontSize:13 }}>
                {mode === "spread"
                  ? "The baseline curve has no points in common with the others."
                  : "Those curves hold no rate points."}
              </div>
            )}

            {!seriesBusy && plot && (
              <>
                <div ref={plotBox}>
                <svg ref={svgRef} viewBox={`0 0 ${VB.w} ${VB.h}`}
                     width={VB.w} height={VB.h}
                     style={{ display:"block", overflow:"visible" }}
                     onMouseMove={onMove} onMouseLeave={() => setHover(null)}
                     role="img"
                     aria-label={`${indexname} forward curves, `
                       + (mode === "spread" ? "spread against " + fmtDate(baseline)
                                            : "rates as uploaded")}>
                  <defs>
                    <filter id="fc-glow" x="-5%" y="-20%" width="110%" height="140%">
                      <feGaussianBlur stdDeviation="2.5" result="b" />
                      <feMerge><feMergeNode in="b" /><feMergeNode in="SourceGraphic" /></feMerge>
                    </filter>
                  </defs>

                  {/* Gridlines and axes stay recessive — they orient, they do
                      not compete with the data. */}
                  {plot.yTicks.map(t => (
                    <g key={t}>
                      <line x1={PAD.l} x2={VB.w - PAD.r}
                            y1={plot.sy(t)} y2={plot.sy(t)}
                            stroke={T.grid} strokeWidth="1" />
                      <text x={PAD.l - 8} y={plot.sy(t) + 3.5} textAnchor="end"
                            fontSize="10.5" fill={T.muted}
                            style={{ fontVariantNumeric:"tabular-nums" }}>
                        {mode === "spread" ? bps(t) : (t * 100).toFixed(2) + "%"}
                      </text>
                    </g>
                  ))}

                  {/* THE AXIS SAYS WHAT ITS NUMBERS ARE. Rendered, the spread
                      axis read "+0.0 / −5.0 / −10.0" with no unit anywhere on
                      the plot — which is a number a reader has to guess at, and
                      basis points and percent differ by a factor of a hundred. */}
                  <text x={PAD.l} y={PAD.t - 3} fontSize="10"
                        fill={T.muted} textAnchor="start">
                    {mode === "spread"
                      ? `basis points vs ${fmtDate(baseline)}`
                      : "rate"}
                  </text>

                  {/* Zero is the baseline itself in spread mode — a real
                      reference line, drawn firmer than the grid. */}
                  {mode === "spread" && plot.yLo < 0 && plot.yHi > 0 && (
                    <line x1={PAD.l} x2={VB.w - PAD.r}
                          y1={plot.sy(0)} y2={plot.sy(0)}
                          stroke={T.gridFirm} strokeWidth="1.5" />
                  )}

                  {/* x labels: first, last, and a few between — never one per
                      point, which on a 360-month curve is a black smear. */}
                  {[0, 0.25, 0.5, 0.75, 1].map(f => {
                    const x = plot.xLo + (plot.xHi - plot.xLo) * f;
                    return (
                      <text key={f} x={plot.sx(x)} y={VB.h - PAD.b + 16}
                            textAnchor={f === 0 ? "start" : f === 1 ? "end" : "middle"}
                            fontSize="10.5" fill={T.muted}>
                        {monthLabel(ymOf(new Date(x).toISOString().slice(0, 10)))}
                      </text>
                    );
                  })}

                  {/* the crosshair, behind the lines */}
                  {hover && (
                    <line x1={plot.sx(hover.x)} x2={plot.sx(hover.x)}
                          y1={PAD.t} y2={VB.h - PAD.b}
                          stroke={T.gridFirm} strokeWidth="1" />
                  )}

                  {/* the curves */}
                  {plot.lines.map(l => (
                    <path key={l.curvedate} d={l.d} fill="none"
                          stroke={sc(l.style.color)}
                          strokeWidth={l.style.width || (l.isBaseline ? 1.5 : 2)}
                          strokeDasharray={l.style.dash || undefined}
                          strokeLinejoin="round" strokeLinecap="round"
                          filter={T.dark && !l.isBaseline ? "url(#fc-glow)" : undefined}
                          opacity={l.isBaseline ? 0.55 : 1} />
                  ))}

                  {/* Markers only where the reader is looking. A dot on every
                      point of every curve is noise; a dot on the crosshair is
                      the value being read. The surface-coloured ring is the 2px
                      spacer that keeps two coincident markers legible. */}
                  {hover && plot.lines.map(l => {
                    const y = at(l, hover.x);
                    if (y === null) return null;
                    return (
                      <circle key={l.curvedate} cx={plot.sx(hover.x)} cy={plot.sy(y)}
                              r="4.5" fill={sc(l.style.color)}
                              stroke={T.surface} strokeWidth="2" />
                    );
                  })}

                  {/* DIRECT LABELS at the end of each line. This is the relief
                      the palette validator requires for the pale members of the
                      brand ramp: identity never rests on colour alone.
                      Positions come from the declutter pass above; a leader
                      line appears whenever a label had to be moved to fit. */}
                  {plot.lines.map(l => (
                    <g key={l.curvedate}>
                      {l.labelMoved && (
                        <polyline
                          points={`${plot.sx(l.end.x)},${plot.sy(l.end.y)} `
                                + `${plot.sx(l.end.x) + 4},${l.labelY} `
                                + `${plot.sx(l.end.x) + 7},${l.labelY}`}
                          fill="none" stroke={sc(l.style.color)} strokeWidth="1"
                          opacity="0.6" />
                      )}
                      {/* A HALO, because not every label sits in the right
                          margin. The Actual series stops where history stops,
                          so its label lands INSIDE the plot, on top of the
                          curves — rendered without this it read "Actua" behind
                          a marker dot.

                          paint-order puts the stroke under the fill, so the
                          surface-coloured stroke becomes a backdrop rather than
                          an outline. T.surface carries a literal fallback: a bare
                          var() here would drop the stroke and the halo would do
                          nothing at all. */}
                      <text x={plot.sx(l.end.x) + 9} y={l.labelY + 3.5}
                            fontSize="10.5" fill={T.ink}
                            stroke={T.surface} strokeWidth="3.5"
                            paintOrder="stroke" strokeLinejoin="round"
                            style={{ fontVariantNumeric:"tabular-nums" }}>
                        {l.label || fmtDate(l.curvedate)}
                      </text>
                    </g>
                  ))}
                </svg>
                </div>

                {/* the tooltip, as HTML under the plot — a fixed row rather than
                    a floating box, so it never covers the curves it describes */}
                <div style={{ marginTop:8, minHeight:34, padding:"6px 8px",
                              borderRadius:6, background:T.panel,
                              fontSize:12, color:T.ink,
                              display:"flex", flexWrap:"wrap", gap:14,
                              alignItems:"center" }}>
                  {hover ? (
                    <>
                      <strong style={{ minWidth:86 }}>
                        {fmtDate(new Date(hover.x).toISOString().slice(0, 10))}
                      </strong>
                      {plot.lines.map(l => {
                        const y = at(l, hover.x);
                        return (
                          <span key={l.curvedate}
                                style={{ display:"inline-flex", alignItems:"center", gap:5 }}>
                            <svg width="16" height="8" aria-hidden="true">
                              <line x1="0" y1="4" x2="16" y2="4" stroke={sc(l.style.color)}
                                    strokeWidth="2"
                                    strokeDasharray={l.style.dash || undefined} />
                            </svg>
                            <span style={{ color:T.muted }}>
                              {l.label || fmtDate(l.curvedate)}</span>
                            <span style={{ fontVariantNumeric:"tabular-nums" }}>
                              {y === null ? "—" : mode === "spread" ? bps(y) + " bps" : pct(y)}
                            </span>
                          </span>
                        );
                      })}
                    </>
                  ) : (
                    <span style={{ color:T.muted }}>
                      Hover the chart to read every curve at one date.
                    </span>
                  )}
                </div>
              </>
            )}
          </Card>

          {/* ── the numbers ───────────────────────────────────────────── */}
          {showTable && plot && (
            <Card style={{ padding:0, overflow:"hidden" }}>
              <div style={{ overflowX:"auto", maxHeight:"46vh" }}>
                <table style={{ borderCollapse:"collapse", width:"100%", fontSize:12,
                                fontVariantNumeric:"tabular-nums" }}>
                  <thead>
                    <tr>
                      <th style={thStyle(true)}>Rate date</th>
                      {plot.lines.map(l => (
                        <th key={l.curvedate} style={{ ...thStyle(false), textAlign:"right" }}>
                          {l.label || fmtDate(l.curvedate)}
                          {l.isBaseline ? " (base)" : ""}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {plot.xs.map(x => (
                      <tr key={x}>
                        <td style={tdStyle}>
                          {fmtDate(new Date(x).toISOString().slice(0, 10))}
                        </td>
                        {plot.lines.map(l => {
                          const v = at(l, x);
                          return (
                            <td key={l.curvedate} style={{ ...tdStyle, textAlign:"right" }}>
                              {v === null ? "—"
                                : mode === "spread" ? bps(v) : pct(v)}
                            </td>
                          );
                        })}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </Card>
          )}

          {/* THE UNITS CALL, STATED. ForwardRates holds fractions; Rates might
              hold either, and being wrong by 100x draws a chart nobody
              questions. The endpoint decides by comparing the two for the same
              index, and says so here so a wrong call is visible rather than
              silent. */}
          {!!(series && series.actual && series.actual.scale === "percent") && (
            <div style={{ fontSize:12, color:INK_MUTED }}>
              dbo.Rates appears to store this index as a percent
              {series.actual.comparedRatio
                ? ` (about ${series.actual.comparedRatio}x the forward values)` : ""}
              , so it has been divided by 100 to sit on the same axis. If the
              actual line looks wrong by a factor of a hundred, that call is why.
            </div>
          )}

          {!!(series && series.actual && series.actual.sources
              && series.actual.sources.length > 1) && (
            <div style={{ fontSize:12, color:INK_MUTED }}>
              dbo.Rates holds more than one source for {indexname} over this span
              ({series.actual.sources.join(", ")}). The most recently entered row
              for each date is the one plotted.
            </div>
          )}

          {!!(series && series.actual && series.actual.availableIndexes) && (
            <div style={{ fontSize:12, color:INK_MUTED }}>
              No actual rates in dbo.Rates named <strong>{indexname}</strong> over
              this span. The names it does carry are:{" "}
              {series.actual.availableIndexes.join(", ") || "none"}. The two
              tables spell the column differently (index_name vs indexname) and
              may spell the value differently too.
            </div>
          )}

          {!!(series && series.actualError) && (
            <div style={{ fontSize:12, color:INK_MUTED }}>
              The actual rate could not be read: {series.actualError}
            </div>
          )}

          {!!(series && series.empty && series.empty.length) && (
            <div style={{ fontSize:12, color:INK_MUTED }}>
              No points loaded for {series.empty.map(d => fmtDate(d)).join(", ")}.
            </div>
          )}

          {/* ── rates by month: actual + each selected curve ─────────────────────────────────── */}
          <ActualByMonth indexname={indexname} hist={hist} busy={histBusy}
                         curves={(series && series.series) || []} />
        </>
      )}
    </div>
  );
};

// ── RATES BY MONTH: ACTUAL AND EACH SELECTED CURVE ─────────────────────────
//
// One row per month. Actual comes from dbo.Rates — every month on file, not
// only the plotted span: the month-end print, and its move against the month
// before. Then a Forecast column per selected curve, that curve's points
// averaged by month, so two uploads and what actually happened read side by
// side. Where a month has an actual, each forecast cell also shows how far it
// sat from the month-end print, in basis points — the forecast miss.
//
// Everything compared against the actual uses the MONTH-END print, the one
// figure shown, so the change and the miss tie to the number on screen.
//
// Curves always show RATES here, whatever the chart's Rates / Spread mode:
// the table is for reading levels off.
//
// Newest month first, with a row per year as a separator.
const ActualByMonth = ({ indexname, hist, busy, curves }) => {
  const actual = useMemo(() => (hist && hist.months) || [], [hist]);

  // each selected curve → { ym: average }
  const curveCols = useMemo(() => (curves || []).map((c, i) => {
    const acc = {};
    (c.points || []).forEach(p => {
      const ym = String(p.ratedate).slice(0, 7);
      const a = acc[ym] || (acc[ym] = { s: 0, n: 0 });
      a.s += num(p.rate); a.n += 1;
    });
    const avg = {};
    Object.keys(acc).forEach(k => { avg[k] = acc[k].s / acc[k].n; });
    return { curvedate: c.curvedate, style: styleAt(i), avg };
  }), [curves]);

  const byYear = useMemo(() => {
    const act = {};
    actual.forEach(m => { act[m.ym] = m; });
    const yms = new Set(actual.map(m => m.ym));
    curveCols.forEach(c => Object.keys(c.avg).forEach(k => yms.add(k)));
    const sorted = [...yms].sort().reverse();

    const out = [];
    sorted.forEach(ym => {
      const y = ym.slice(0, 4);
      let g = out[out.length - 1];
      if (!g || g.year !== y) { g = { year: y, months: [] }; out.push(g); }
      const m = act[ym] || { ym, avg: null, monthEnd: null, min: null, max: null, days: 0 };
      // The month before by CALENDAR, not by row — rows now include forecast
      // months with no actual, so the next row is not necessarily the prior.
      const [yy, mm] = ym.split("-").map(Number);
      const prevYm = mm === 1 ? `${yy - 1}-12` : `${yy}-${String(mm - 1).padStart(2, "0")}`;
      const prev = act[prevYm];
      g.months.push({ ...m, ym,
        chg: prev && prev.monthEnd != null && m.monthEnd != null ? m.monthEnd - prev.monthEnd : null,
        curves: curveCols.map(c => (c.avg[ym] == null ? null : c.avg[ym])) });
    });
    return out;
  }, [actual, curveCols]);

  const hasRows = byYear.length > 0;
  const csv = () => {
    const rows = [];
    byYear.forEach(g => g.months.forEach(m => {
      const r = { month: m.ym, actual_monthend: m.monthEnd,
                  change_bps: m.chg == null ? "" : m.chg * 10000 };
      curveCols.forEach((c, i) => { r["forecast_" + c.curvedate] = m.curves[i] == null ? "" : m.curves[i]; });
      rows.push(r);
    }));
    exportCSV(rows, ["month", "actual_monthend", "change_bps",
                     ...curveCols.map(c => "forecast_" + c.curvedate)],
      `rates_${indexname}_by_month.csv`);
  };

  const mo = ym => monthLabel(ym).replace(/ \d\d$/, "");
  const yrTd = { ...tdStyle, fontWeight:700, background:SURFACE_MUTED };
  const r = { ...tdStyle, textAlign:"right" };
  // Two shaded bands over the columns, so Actual and Forecast read as two
  // groups at a glance: brand cyan for what happened, brand amber for what
  // was expected. The column row under them is the usual shaded header.
  const BAND_H = 30;
  const BAND = {
    actual:   { background:B.cyanLight,  color:B.cyanDark },
    forecast: { background:B.amberLight, color:B.amberDark },
  };
  const bandTh = (label, span, tone, first) => (
    <th colSpan={span} style={{ ...thStyle(false), ...BAND[tone], textAlign:"center",
                                fontSize:11, fontWeight:700, letterSpacing:"0.08em",
                                // A fixed height, so the column row can stick
                                // exactly under it with no sliver of body between.
                                height:BAND_H, boxSizing:"border-box", lineHeight:"16px",
                                padding:"7px 10px", borderBottom:"none",
                                borderLeft: first ? "none" : "2px solid " + SURFACE }}>{label}</th>
  );
  const colTh = (first, tone, lead) => ({ ...thStyle(first), top:BAND_H - 1, background:SURFACE_MUTED,
    ...(tone ? { boxShadow:`inset 0 2px 0 ${BAND[tone].background}` } : null),
    ...(lead ? { borderLeft:"2px solid " + SURFACE } : null) });
  const sep = { borderLeft:"2px solid " + HAIRLINE };

  return (
    <Card style={{ padding:0, overflow:"hidden" }}>
      <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between",
                    gap:10, padding:"12px 14px" }}>
        <div>
          <SectionTitle>{indexname} rates by month</SectionTitle>
          <div style={{ fontSize:12, color:INK_MUTED, marginTop:2 }}>
            Actual is the month-end print from the rates table, every month on file, and its
            change from the month before. Forecast is each selected curve averaged by month —
            where an actual exists, the small figure is the curve&rsquo;s miss against it in
            basis points.
          </div>
        </div>
        <span style={{ flexShrink:0 }}><Btn onClick={csv} disabled={!hasRows}>Export CSV</Btn></span>
      </div>
      {busy && !actual.length ? <Spinner /> : null}
      {!busy && hist && hist.error ? (
        <div style={{ padding:"0 14px 10px", fontSize:12, color:INK_MUTED }}>
          The actual rates could not be read: {hist.error}
        </div>
      ) : null}
      {!busy && hist && !hist.error && !actual.length ? (
        <div style={{ padding:"0 14px 10px", fontSize:12, color:INK_MUTED }}>
          No actual rates on file named <strong>{indexname}</strong>.
          {hist.availableIndexes ? <> The names the rates table carries are:{" "}
            {hist.availableIndexes.join(", ") || "none"}.</> : null}
        </div>
      ) : null}
      {hasRows ? (
        <div style={{ overflowX:"auto", maxHeight:"52vh" }}>
          <table style={{ borderCollapse:"collapse", width:"100%", fontSize:12,
                          fontVariantNumeric:"tabular-nums" }}>
            <thead>
              <tr>
                <th rowSpan={2} style={{ ...thStyle(true), background:SURFACE_MUTED,
                                         verticalAlign:"bottom" }}>Month</th>
                {bandTh("ACTUAL", 2, "actual", true)}
                {curveCols.length ? bandTh("FORECAST (monthly average)", curveCols.length, "forecast") : null}
              </tr>
              <tr>
                <th style={colTh(false, "actual")}>Month-end</th>
                <th style={colTh(false, "actual")}>Change (bps)</th>
                {curveCols.map((c, i) => (
                  <th key={c.curvedate} style={colTh(false, "forecast", i === 0)}>
                    <span style={{ display:"inline-flex", alignItems:"center", gap:5 }}>
                      <svg width="16" height="8" aria-hidden="true">
                        <line x1="0" y1="4" x2="16" y2="4" stroke={c.style.color} strokeWidth="2"
                              strokeDasharray={c.style.dash || undefined} />
                      </svg>
                      {fmtDate(c.curvedate)}
                    </span>
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {byYear.map(g => (
                <Fragment key={g.year}>
                  <tr>
                    <td style={yrTd} colSpan={3 + curveCols.length}>{g.year}</td>
                  </tr>
                  {g.months.map(m => (
                    <tr key={m.ym}>
                      <td style={{ ...tdStyle, paddingLeft:22 }}>{mo(m.ym)}</td>
                      <td style={{ ...r, fontWeight:600 }}>{m.monthEnd == null ? "—" : pct(m.monthEnd)}</td>
                      <td style={{ ...r,
                                   color: m.chg == null || Math.abs(m.chg) < 0.00005 ? INK_MUTED
                                        : m.chg > 0 ? B.cyanDark : B.amberDark }}>
                        {m.chg == null ? "—" : bps(m.chg)}
                      </td>
                      {m.curves.map((v, i) => (
                        <td key={i} style={{ ...r, ...(i ? null : sep) }}>
                          {v == null ? <span style={{ color:INK_MUTED }}>—</span> : pct(v)}
                          {v != null && m.monthEnd != null ? (
                            <span style={{ fontSize:10.5, color:INK_MUTED, marginLeft:6 }}>
                              {bps(v - m.monthEnd)}</span>
                          ) : null}
                        </td>
                      ))}
                    </tr>
                  ))}
                </Fragment>
              ))}
            </tbody>
          </table>
        </div>
      ) : null}
      {hist && hist.scale === "percent" ? (
        <div style={{ padding:"8px 14px", fontSize:11, color:INK_MUTED }}>
          The rates table appears to store {indexname} as a percent, so it has been divided
          by 100 to read the same as the forward curves.
        </div>
      ) : null}
    </Card>
  );
};

const thStyle = first => ({
  textAlign: first ? "left" : "right", padding:"8px 10px", whiteSpace:"nowrap",
  fontSize:10.5, fontWeight:600, textTransform:"uppercase", letterSpacing:"0.04em",
  color:INK_PRIMARY, background:SURFACE, position:"sticky", top:0, zIndex:1,
  borderBottom:"1.5px solid " + HAIRLINE_FIRM,
});
const tdStyle = { padding:"6px 10px", borderBottom:"1px solid " + HAIRLINE,
                  whiteSpace:"nowrap" };

const SectionTitle = ({ children }) => (
  <div style={{ fontSize:12, fontWeight:700, color:INK_PRIMARY,
                textTransform:"uppercase", letterSpacing:"0.06em" }}>
    {children}
  </div>
);

export default ForwardCurvesPage;
